import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import api from '../services/api';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const FIELD_SOURCES = [
  { value: 'manual_entry', label: 'Fillable (type when printing)' },
  { value: 'static_text', label: 'Static Text (always same value)' },
  { value: 'sign_here_customer', label: 'Customer Signs Here (marker)' },
  { value: 'sign_here_agent', label: 'Agent Signs Here (marker)' },
  { value: 'today_date', label: "Today's Date (auto)" },
  { value: 'sale_date', label: 'Sale Date' },
  { value: 'buyer_name', label: 'Buyer Name' },
  { value: 'buyer_address', label: 'Buyer Address' },
  { value: 'buyer_city', label: 'Buyer City' },
  { value: 'buyer_state', label: 'Buyer State' },
  { value: 'buyer_zip', label: 'Buyer ZIP' },
  { value: 'buyer_city_state_zip', label: 'Buyer City/State/ZIP' },
  { value: 'buyer_phone', label: 'Buyer Phone' },
  { value: 'buyer_email', label: 'Buyer Email' },
  { value: 'buyer_dl_number', label: 'Buyer DL Number' },
  { value: 'buyer_dl_state', label: 'Buyer DL State' },
  { value: 'vehicle_year', label: 'Vehicle Year' },
  { value: 'vehicle_make', label: 'Vehicle Make' },
  { value: 'vehicle_model', label: 'Vehicle Model' },
  { value: 'vehicle_vin', label: 'Vehicle VIN' },
  { value: 'vehicle_color', label: 'Vehicle Color' },
  { value: 'vehicle_mileage', label: 'Vehicle Mileage' },
  { value: 'vehicle_stock', label: 'Stock Number' },
  { value: 'vehicle_description', label: 'Vehicle Description (Year Make Model)' },
  { value: 'sale_price', label: 'Sale Price' },
  { value: 'trade_in_value', label: 'Trade-In Value' },
  { value: 'taxable_amount', label: 'Taxable Amount' },
  { value: 'sales_tax', label: 'Sales Tax' },
  { value: 'doc_fee', label: 'Doc Fee' },
  { value: 'title_fee', label: 'Title Fee' },
  { value: 'temp_tag_fee', label: 'Temp Tag Fee' },
  { value: 'total_due', label: 'Total Due' },
  { value: 'dealer_name', label: 'Dealer Name' },
  { value: 'dealer_address', label: 'Dealer Address' },
  { value: 'dealer_city_state_zip', label: 'Dealer City/State/ZIP' },
  { value: 'dealer_phone', label: 'Dealer Phone' },
  { value: 'dealer_license', label: 'Dealer License #' },
];

// A field "needs a name" when it's a fill-in/static field with no real label —
// blank or the literal placeholder 'manual_entry' (the print screen can't ask a
// real question for those).
const fieldNeedsName = (f) => {
  const name = (f.fieldName || '').trim();
  return (f.fieldSource === 'manual_entry' || f.fieldSource === 'static_text') &&
    (!name || name === 'manual_entry');
};

// initialFieldId: opened from the print screen's "name it" link — jump straight to
// that field's page with it selected so it's obvious which box on the form it is.
const FormFieldEditor = ({ template, onClose, onSave, initialFieldId = null }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 612, height: 792 });
  const [renderScale, setRenderScale] = useState(1.0);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [legacyImported, setLegacyImported] = useState(false);
  // White-Out mode: draw rectangles to hide pre-printed junk on the original PDF
  const [editorMode, setEditorMode] = useState('fields'); // 'fields' | 'whiteout'
  const [whiteoutZones, setWhiteoutZones] = useState(() => Array.isArray(template.redaction_zones) ? template.redaction_zones : []);
  const [savingWhiteout, setSavingWhiteout] = useState(false);
  const whiteoutDrawRef = useRef({ active: false, startX: 0, startY: 0 });
  const [whiteoutPreview, setWhiteoutPreview] = useState(null); // {x,y,w,h} in canvas px while dragging
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Drag state tracked in refs (no re-renders during drag)
  const dragRef = useRef({ active: false, fieldId: null, startMouseX: 0, startMouseY: 0, startX: 0, startY: 0 });
  // Resize state (width = drag right edge, height = drag bottom edge)
  const resizeRef = useRef({ active: false, type: null, fieldId: null, startMouseX: 0, startMouseY: 0, startWidth: 120, startHeight: 18 });

  // Load existing fields
  useEffect(() => {
    const loadFields = async () => {
      try {
        const data = await api.getFormTemplateFields(template.id);
        setFields(data.map(f => ({
          id: f.id,
          fieldName: f.field_name,
          fieldSource: f.field_source,
          pageNumber: f.page_number || 1,
          xPosition: parseFloat(f.x_position),
          yPosition: parseFloat(f.y_position),
          fontSize: f.font_size || 10,
          fontName: f.font_name || 'Helvetica',
          maxWidth: f.max_width ? parseFloat(f.max_width) : null,
          fieldHeight: f.field_height ? parseFloat(f.field_height) : null,
          alignment: f.alignment || 'left',
          transform: f.transform || null,
          isBold: f.is_bold || false,
        })));
      } catch (err) {
        console.error('Failed to load fields:', err);
      } finally {
        setLoading(false);
      }
    };
    loadFields();
  }, [template.id]);

  // Load PDF with PDF.js
  useEffect(() => {
    const loadPdf = async () => {
      setPdfLoading(true);
      try {
        let pdfUrl = template.blank_file_url || template.original_file_url;
        if (!pdfUrl && template.id) pdfUrl = api.getBlankTemplatePdfUrl(template.id);
        if (!pdfUrl) { setPdfLoading(false); return; }

        const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
      } catch (err) {
        console.error('Failed to load PDF:', err);
      } finally {
        setPdfLoading(false);
      }
    };
    loadPdf();
  }, [template]);

  // Render current page
  useEffect(() => {
    const renderPage = async () => {
      if (!pdfDoc || !canvasRef.current) return;
      try {
        const page = await pdfDoc.getPage(currentPage);
        const viewport = page.getViewport({ scale: renderScale });
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        const orig = page.getViewport({ scale: 1 });
        setPdfDimensions({ width: orig.width, height: orig.height });
        await page.render({ canvasContext: ctx, viewport }).promise;
      } catch (err) {
        console.error('Failed to render page:', err);
      }
    };
    renderPage();
  }, [pdfDoc, currentPage, renderScale]);

  // Global mouse handlers for dragging and resizing
  useEffect(() => {
    const onMouseMove = (e) => {
      // Handle resize
      const r = resizeRef.current;
      if (r.active) {
        if (r.type === 'width') {
          const dx = e.clientX - r.startMouseX;
          const newWidth = Math.max(20, Math.round(r.startWidth + dx / renderScale));
          setFields(prev => prev.map(f => f.id === r.fieldId ? { ...f, maxWidth: newWidth } : f));
        } else if (r.type === 'height') {
          const dy = e.clientY - r.startMouseY;
          // Dragging DOWN increases canvas y = decreasing box top = increasing height
          const newHeight = Math.max(8, Math.round(r.startHeight + dy / renderScale));
          setFields(prev => prev.map(f => f.id === r.fieldId ? { ...f, fieldHeight: newHeight } : f));
        }
        return;
      }
      // Handle move drag
      const d = dragRef.current;
      if (!d.active) return;
      const dx = e.clientX - d.startMouseX;
      const dy = e.clientY - d.startMouseY;
      const pdfDx = dx / renderScale;
      const pdfDy = -dy / renderScale; // Y is inverted (canvas top → PDF bottom)
      const newX = Math.max(0, d.startX + pdfDx);
      const newY = Math.max(0, d.startY + pdfDy);
      setFields(prev => prev.map(f => f.id === d.fieldId
        ? { ...f, xPosition: Math.round(newX), yPosition: Math.round(newY) }
        : f
      ));
    };
    const onMouseUp = () => {
      dragRef.current.active = false;
      resizeRef.current.active = false;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [renderScale]);

  // Keyboard: Ctrl+S to save, Escape to deselect
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); saveFields(); }
      if (e.key === 'Escape') setSelectedFieldId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // One-time import of OLD-editor mappings: forms mapped with the retired two-step editor
  // store mappings in a legacy percent-based format this editor couldn't see (an empty
  // field list here looked exactly like "my mappings are gone"). Convert them into normal
  // editable fields; nothing is stored until Save Fields is pressed.
  useEffect(() => {
    if (loading || pdfLoading || legacyImported || fields.length > 0) return;
    const legacy = Array.isArray(template.field_mappings) ? template.field_mappings : [];
    if (legacy.length === 0) return;
    const W = pdfDimensions.width || 612;
    const H = pdfDimensions.height || 792;
    setFields(legacy.map((m, i) => ({
      id: `legacy_${i}`,
      fieldName: m.fieldName || '',
      fieldSource: m.field || 'manual_entry',
      pageNumber: m.page || 1,
      xPosition: Math.round((m.percentX || 0) * W),
      yPosition: Math.round(H * (1 - (m.percentY || 0))),
      fontSize: m.fontSize || 9,
      fontName: 'Helvetica',
      maxWidth: m.maxWidth || null,
      fieldHeight: null,
      alignment: 'left',
      transform: m.format || null,
      isBold: m.bold || false,
      isNew: true,
    })));
    setLegacyImported(true);
  }, [loading, pdfLoading, legacyImported, fields.length, pdfDimensions, template.field_mappings]);

  // Jump to the field the print screen pointed at (once, after fields load)
  const [jumpedToInitial, setJumpedToInitial] = useState(false);
  useEffect(() => {
    if (loading || jumpedToInitial || initialFieldId == null) return;
    const target = fields.find(f => String(f.id) === String(initialFieldId));
    if (target) {
      setSelectedFieldId(target.id);
      setCurrentPage(target.pageNumber || 1);
    }
    setJumpedToInitial(true);
  }, [loading, jumpedToInitial, initialFieldId, fields]);

  const addField = (fieldSource = 'vehicle_vin') => {
    const isMarker = String(fieldSource).startsWith('sign_here_');
    const newField = {
      id: `new_${Date.now()}`,
      fieldName: isMarker ? (fieldSource === 'sign_here_agent' ? 'Agent signs here' : 'Customer signs here') : '',
      fieldSource,
      pageNumber: currentPage,
      xPosition: 100,
      yPosition: pdfDimensions.height - 100,
      fontSize: isMarker ? 8 : 9,
      fontName: 'Helvetica',
      maxWidth: isMarker ? 110 : null,
      fieldHeight: null,
      alignment: 'left',
      transform: null,
      isBold: false,
      isNew: true,
    };
    setFields(prev => [...prev, newField]);
    setSelectedFieldId(newField.id);
  };

  const updateField = useCallback((id, updates) => {
    setFields(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  }, []);

  const deleteField = async (id) => {
    if (!window.confirm('Delete this field?')) return;
    const field = fields.find(f => f.id === id);
    if (field && !field.isNew) {
      try { await api.deleteFormTemplateField(template.id, id); }
      catch (err) { alert('Failed to delete: ' + err.message); return; }
    }
    setFields(prev => prev.filter(f => f.id !== id));
    if (selectedFieldId === id) setSelectedFieldId(null);
  };

  const saveFields = async () => {
    // Fillable and static fields MUST have real names — unnamed ones show up in the
    // print screen as useless "manual_entry" boxes that collide with each other.
    const needsName = fields.filter(fieldNeedsName);
    if (needsName.length > 0) {
      setSelectedFieldId(needsName[0].id);
      if (needsName[0].pageNumber) setCurrentPage(needsName[0].pageNumber);
      alert(`${needsName.length} fillable/static field${needsName.length === 1 ? ' has' : 's have'} no name.\n\nName ${needsName.length === 1 ? 'it' : 'them'} (right panel) so the print screen can ask a real question instead of showing "manual_entry". The first one is now selected.`);
      return;
    }
    setSaveStatus('saving');
    try {
      const valid = fields.filter(f => f.fieldSource && f.xPosition !== undefined && f.yPosition !== undefined);
      await api.bulkUpdateFormTemplateFields(template.id, valid.map(f => ({
        fieldName: f.fieldSource === 'manual_entry' ? (f.fieldName || f.fieldSource) : (f.fieldName || f.fieldSource),
        fieldSource: f.fieldSource,
        pageNumber: f.pageNumber || 1,
        xPosition: f.xPosition,
        yPosition: f.yPosition,
        fontSize: f.fontSize || 9,
        fontName: f.fontName || 'Helvetica',
        maxWidth: f.maxWidth || null,
        fieldHeight: f.fieldHeight || null,
        alignment: f.alignment || 'left',
        transform: f.transform || null,
        isBold: f.isBold || false,
      })));
      setSaveStatus('saved');
      onSave?.();
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err) {
      console.error('Save failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const generatePreview = async () => {
    setPreviewLoading(true);
    try {
      // Save first (silently — don't wait for status animation)
      const valid = fields.filter(f => f.fieldSource && f.xPosition !== undefined && f.yPosition !== undefined);
      await api.bulkUpdateFormTemplateFields(template.id, valid.map(f => ({
        fieldName: f.fieldName || f.fieldSource,
        fieldSource: f.fieldSource,
        pageNumber: f.pageNumber || 1,
        xPosition: f.xPosition,
        yPosition: f.yPosition,
        fontSize: f.fontSize || 9,
        fontName: f.fontName || 'Helvetica',
        maxWidth: f.maxWidth || null,
        fieldHeight: f.fieldHeight || null,
        alignment: f.alignment || 'left',
        transform: f.transform || null,
        isBold: f.isBold || false,
      })));

      const result = await api.previewFormTemplate(template.id);
      if (result.preview) {
        const binary = atob(result.preview);
        const bytes = new Uint8Array(binary.length).map((_, i) => binary.charCodeAt(i));
        const blob = new Blob([bytes], { type: 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2500);
      } else {
        alert('No preview generated. Add fields and position them first.');
      }
    } catch (err) {
      alert('Preview failed: ' + err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  // ----- White-Out mode: drag rectangles over junk; saved zones regenerate the blank -----
  const canvasPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startWhiteout = (e) => {
    if (editorMode !== 'whiteout' || !canvasRef.current) return;
    const p = canvasPoint(e);
    whiteoutDrawRef.current = { active: true, startX: p.x, startY: p.y };
    setWhiteoutPreview({ x: p.x, y: p.y, w: 0, h: 0 });
  };

  const moveWhiteout = (e) => {
    const d = whiteoutDrawRef.current;
    if (!d.active) return;
    const p = canvasPoint(e);
    setWhiteoutPreview({
      x: Math.min(d.startX, p.x),
      y: Math.min(d.startY, p.y),
      w: Math.abs(p.x - d.startX),
      h: Math.abs(p.y - d.startY),
    });
  };

  const endWhiteout = () => {
    const d = whiteoutDrawRef.current;
    if (!d.active) return;
    whiteoutDrawRef.current.active = false;
    const pv = whiteoutPreview;
    setWhiteoutPreview(null);
    if (!pv || pv.w < 4 || pv.h < 4) return;
    const H = pdfDimensions.height;
    setWhiteoutZones(z => [...z, {
      page: currentPage,
      x: Math.round(pv.x / renderScale),
      y: Math.round(H - (pv.y + pv.h) / renderScale), // store bottom-left in PDF points
      width: Math.round(pv.w / renderScale),
      height: Math.round(pv.h / renderScale),
    }]);
  };

  const saveWhiteout = async () => {
    setSavingWhiteout(true);
    try {
      await api.saveRedactionZones(template.id, whiteoutZones);
      alert('White-out saved — the printable blank was regenerated with these areas hidden.');
      onSave?.();
    } catch (err) {
      alert('Failed to save white-out: ' + err.message + '\n(The original PDF file must be available on this server.)');
    } finally {
      setSavingWhiteout(false);
    }
  };

  // Click on canvas to position selected field — centers field on click point
  const handleCanvasClick = useCallback((e) => {
    if (editorMode !== 'fields') return;
    if (!selectedFieldId || !canvasRef.current) return;
    if (dragRef.current.active) return; // ignore click-end-of-drag
    if (resizeRef.current.active) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const cssScaleX = canvas.width / rect.width;
    const cssScaleY = canvas.height / rect.height;
    const canvasPx = (e.clientX - rect.left) * cssScaleX;
    const canvasPy = (e.clientY - rect.top) * cssScaleY;
    const pdfX = canvasPx / renderScale;
    // Get the current field's raw height so we can CENTER the field on the click point
    // (instead of putting the field bottom at the click, which felt off)
    const field = fields.find(f => f.id === selectedFieldId);
    const rawHeight = field ? (field.fieldHeight || Math.max((field.fontSize || 9) * 2, 14)) : 14;
    const pdfY = pdfDimensions.height - (canvasPy / renderScale) - rawHeight / 2;
    updateField(selectedFieldId, {
      xPosition: Math.round(Math.max(0, pdfX)),
      yPosition: Math.round(Math.max(0, pdfY)),
      pageNumber: currentPage,
    });
  }, [editorMode, selectedFieldId, fields, renderScale, pdfDimensions, currentPage, updateField]);

  const currentPageFields = fields.filter(f => f.pageNumber === currentPage);
  const selectedField = fields.find(f => f.id === selectedFieldId);

  const isMarkerField = (field) => String(field.fieldSource || '').startsWith('sign_here_');

  const labelFor = (field) => {
    if (field.fieldSource === 'manual_entry') return field.fieldName || 'Fillable';
    if (field.fieldSource === 'static_text') return `"${field.fieldName || 'Static'}"`;
    if (field.fieldSource === 'sign_here_customer') return 'Customer signs here \u2192';
    if (field.fieldSource === 'sign_here_agent') return 'Agent signs here \u2192';
    return FIELD_SOURCES.find(f => f.value === field.fieldSource)?.label || field.fieldSource;
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2">
      <div className="bg-white w-full max-w-6xl rounded-lg shadow-2xl overflow-hidden flex flex-col" style={{ height: '94vh' }}>

        {/* Saved toast overlay */}
        {saveStatus === 'saved' && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[200] bg-green-600 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-2 text-sm font-semibold pointer-events-none animate-bounce">
            ✓ Fields saved successfully!
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[200] bg-red-600 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-2 text-sm font-semibold pointer-events-none">
            ✗ Save failed — check connection and try again
          </div>
        )}

        {/* Header */}
        <div className="px-4 py-2.5 border-b border-neutral-200 bg-neutral-50 flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold leading-tight">Form Field Editor</h2>
            <p className="text-xs text-neutral-400">{template.name} — Click on the PDF to place a field, then drag to fine-tune</p>
            {legacyImported && (
              <p className="text-[11px] text-amber-600 font-medium">
                Imported {fields.length} field{fields.length === 1 ? '' : 's'} from the old editor — press Save Fields to keep them editable here.
              </p>
            )}
          </div>
          <div className="flex gap-2 items-center">
            {saveStatus === 'saving' && <span className="text-xs text-blue-600 font-medium animate-pulse">Saving...</span>}
            <button
              onClick={generatePreview}
              disabled={previewLoading}
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
            >
              {previewLoading ? 'Generating...' : 'Preview'}
            </button>
            <button
              onClick={saveFields}
              disabled={saveStatus === 'saving'}
              className={`px-3 py-1.5 rounded-lg disabled:opacity-50 text-sm font-medium transition-colors ${
                saveStatus === 'saved'
                  ? 'bg-green-600 text-white'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved!' : 'Save Fields'}
            </button>
            <button onClick={onClose} className="px-3 py-1.5 border border-neutral-300 rounded-lg hover:bg-neutral-100 text-sm">Close</button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden min-h-0">

          {/* Left Panel — Field List */}
          <div className="w-56 border-r border-neutral-200 flex flex-col bg-neutral-50 flex-shrink-0">
            <div className="px-3 py-2 border-b border-neutral-200 flex justify-between items-center">
              <span className="font-medium text-xs text-neutral-600">Fields ({fields.length})</span>
              <button onClick={() => addField()} className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">+ Add</button>
            </div>
            <div className="px-3 py-1.5 border-b border-neutral-200 flex gap-1.5">
              <button
                onClick={() => addField('sign_here_customer')}
                title="Place a 'Customer signs here' arrow marker"
                className="flex-1 px-1.5 py-1 bg-red-50 border border-red-200 text-red-700 text-[10px] font-semibold rounded hover:bg-red-100 leading-tight"
              >
                + Customer<br/>sign here
              </button>
              <button
                onClick={() => addField('sign_here_agent')}
                title="Place an 'Agent signs here' arrow marker"
                className="flex-1 px-1.5 py-1 bg-red-50 border border-red-200 text-red-700 text-[10px] font-semibold rounded hover:bg-red-100 leading-tight"
              >
                + Agent<br/>sign here
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {loading ? (
                <p className="text-xs text-center text-neutral-400 py-4">Loading...</p>
              ) : fields.length === 0 ? (
                <p className="text-xs text-center text-neutral-400 py-6">No fields yet.<br/>Click "+ Add" then click on the PDF.</p>
              ) : fields.map(field => (
                <div
                  key={field.id}
                  onClick={() => { setSelectedFieldId(field.id); if (field.pageNumber) setCurrentPage(field.pageNumber); }}
                  className={`p-2 rounded border cursor-pointer text-xs transition-all ${
                    selectedFieldId === field.id ? 'border-blue-500 bg-blue-50'
                      : fieldNeedsName(field) ? 'border-red-300 bg-red-50 hover:border-red-400'
                      : 'border-neutral-200 bg-white hover:border-neutral-300'
                  }`}
                >
                  <div className="font-medium truncate flex items-center gap-1.5">
                    <span className="truncate">{labelFor(field)}</span>
                    {fieldNeedsName(field) && (
                      <span className="text-[9px] bg-red-600 text-white px-1 py-0.5 rounded font-bold whitespace-nowrap" title="Give this fill-in box a real name so the print screen can ask a real question">NEEDS NAME</span>
                    )}
                  </div>
                  <div className="text-neutral-400 text-[10px]">pg{field.pageNumber} · x{field.xPosition} y{field.yPosition}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Center — PDF Canvas */}
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            {/* Toolbar */}
            <div className="px-3 py-1.5 border-b border-neutral-200 bg-white flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}
                  className="px-2 py-0.5 border rounded disabled:opacity-40 text-xs">← Prev</button>
                <span className="text-xs text-neutral-600">Page {currentPage} of {totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
                  className="px-2 py-0.5 border rounded disabled:opacity-40 text-xs">Next →</button>
              </div>
              <div className="flex items-center gap-2">
                {/* Fields vs White-Out mode */}
                <div className="flex items-center border border-neutral-200 rounded overflow-hidden">
                  <button onClick={() => setEditorMode('fields')}
                    className={`px-2 py-0.5 text-xs ${editorMode === 'fields' ? 'bg-blue-600 text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50'}`}>
                    Fields
                  </button>
                  <button onClick={() => setEditorMode('whiteout')}
                    title="Drag boxes over pre-printed junk to hide it on the printable blank"
                    className={`px-2 py-0.5 text-xs ${editorMode === 'whiteout' ? 'bg-red-600 text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50'}`}>
                    White-Out
                  </button>
                </div>
                {editorMode === 'whiteout' && (
                  <button onClick={saveWhiteout} disabled={savingWhiteout}
                    className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
                    {savingWhiteout ? 'Saving…' : `Save White-Out (${whiteoutZones.length})`}
                  </button>
                )}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-neutral-400 mr-1">Zoom:</span>
                  {[0.5, 0.75, 1.0, 1.25, 1.5].map(s => (
                    <button key={s} onClick={() => setRenderScale(s)}
                      className={`px-1.5 py-0.5 text-xs rounded ${renderScale === s ? 'bg-blue-600 text-white' : 'bg-neutral-100 hover:bg-neutral-200'}`}>
                      {Math.round(s * 100)}%
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Canvas area */}
            <div ref={containerRef} className="flex-1 overflow-auto p-4 flex items-start justify-center bg-neutral-200">
              {pdfLoading ? (
                <div className="flex items-center justify-center h-48 text-neutral-500 text-sm">Loading PDF...</div>
              ) : !pdfDoc ? (
                <div className="flex flex-col items-center justify-center h-64 text-center px-8 gap-2">
                  <div className="text-red-500 text-sm font-medium">PDF file not available</div>
                  <div className="text-neutral-500 text-xs max-w-sm">
                    The PDF for this form couldn't be loaded — usually because the file lives on the
                    production server and isn't present in this environment. Field positions are still
                    saved in the database and printing works wherever the file exists.
                  </div>
                </div>
              ) : (
                <div className="relative inline-block select-none">
                  <canvas
                    ref={canvasRef}
                    onClick={handleCanvasClick}
                    onMouseDown={startWhiteout}
                    onMouseMove={moveWhiteout}
                    onMouseUp={endWhiteout}
                    onMouseLeave={endWhiteout}
                    className="shadow-lg block"
                    style={{ cursor: editorMode === 'whiteout' ? 'crosshair' : (selectedFieldId ? 'crosshair' : 'default') }}
                  />

                  {/* White-Out zones (drawn as white boxes with a red edge) + drag preview */}
                  {editorMode === 'whiteout' && whiteoutZones.map((z, i) => {
                    if ((z.page || 1) !== currentPage) return null;
                    const left = z.x * renderScale;
                    const top = (pdfDimensions.height - (z.y + z.height)) * renderScale;
                    return (
                      <div key={i} style={{
                        position: 'absolute', left, top,
                        width: z.width * renderScale, height: z.height * renderScale,
                        backgroundColor: 'rgba(255,255,255,0.9)', border: '1.5px dashed #dc2626', zIndex: 60,
                      }}>
                        <button
                          onClick={() => setWhiteoutZones(zs => zs.filter((_, idx) => idx !== i))}
                          title="Remove this white-out box"
                          style={{ position: 'absolute', top: -8, right: -8, width: 16, height: 16, lineHeight: '13px', fontSize: 11, borderRadius: 8, background: '#dc2626', color: 'white', border: 'none', cursor: 'pointer' }}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                  {whiteoutPreview && (
                    <div style={{
                      position: 'absolute', left: whiteoutPreview.x, top: whiteoutPreview.y,
                      width: whiteoutPreview.w, height: whiteoutPreview.h,
                      backgroundColor: 'rgba(255,255,255,0.7)', border: '1.5px dashed #dc2626', pointerEvents: 'none', zIndex: 61,
                    }} />
                  )}

                  {/* Field marker overlays — rectangle showing exact AcroForm field bounds */}
                  {editorMode === 'fields' && currentPageFields.map(field => {
                    const fontSize = field.fontSize || 9;
                    const rawWidth = field.maxWidth || 120;
                    const rawHeight = field.fieldHeight || Math.max(fontSize * 2, 14);
                    const fieldWidth = rawWidth * renderScale;
                    const fieldHeight = rawHeight * renderScale;
                    const px = field.xPosition * renderScale;
                    // y is from bottom of PDF; convert to canvas top-origin, shift up by fieldHeight
                    const py = (pdfDimensions.height - field.yPosition) * renderScale - fieldHeight;
                    const isSelected = selectedFieldId === field.id;
                    const isMarker = isMarkerField(field);
                    // Unnamed fill-in boxes glow red on the PDF too, so "which box is it?"
                    // answers itself
                    const borderColor = isSelected ? '#2563eb' : (isMarker || fieldNeedsName(field) ? '#dc2626' : '#f59e0b');
                    return (
                      <div
                        key={field.id}
                        style={{
                          position: 'absolute',
                          left: px,
                          top: py,
                          width: fieldWidth,
                          height: fieldHeight,
                          border: `2px solid ${borderColor}`,
                          backgroundColor: isSelected ? 'rgba(37,99,235,0.15)' : (isMarker ? 'rgba(220,38,38,0.12)' : 'rgba(253,224,71,0.25)'),
                          cursor: 'grab',
                          zIndex: isSelected ? 50 : 40,
                          boxSizing: 'border-box',
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setSelectedFieldId(field.id);
                          dragRef.current = {
                            active: true,
                            fieldId: field.id,
                            startMouseX: e.clientX,
                            startMouseY: e.clientY,
                            startX: field.xPosition,
                            startY: field.yPosition,
                          };
                        }}
                      >
                        {/* Label inside the box */}
                        <div
                          className={`absolute top-0 left-0 px-1 text-[9px] leading-tight font-semibold pointer-events-none whitespace-nowrap overflow-hidden ${
                            isSelected ? 'text-blue-700' : (isMarker ? 'text-red-700' : 'text-amber-800')
                          }`}
                          style={{ maxWidth: fieldWidth }}
                        >
                          {labelFor(field)}
                        </div>

                        {/* RIGHT edge resize handle — drag to change width */}
                        <div
                          title="Drag to resize width"
                          style={{
                            position: 'absolute',
                            right: -4,
                            top: 4,
                            bottom: 4,
                            width: 8,
                            cursor: 'ew-resize',
                            backgroundColor: borderColor,
                            borderRadius: 3,
                            zIndex: 10,
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            setSelectedFieldId(field.id);
                            resizeRef.current = {
                              active: true,
                              type: 'width',
                              fieldId: field.id,
                              startMouseX: e.clientX,
                              startMouseY: e.clientY,
                              startWidth: rawWidth,
                              startHeight: rawHeight,
                            };
                          }}
                        />

                        {/* BOTTOM edge resize handle — drag to change height */}
                        <div
                          title="Drag to resize height"
                          style={{
                            position: 'absolute',
                            bottom: -4,
                            left: 4,
                            right: 12,
                            height: 8,
                            cursor: 'ns-resize',
                            backgroundColor: borderColor,
                            borderRadius: 3,
                            zIndex: 10,
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            setSelectedFieldId(field.id);
                            resizeRef.current = {
                              active: true,
                              type: 'height',
                              fieldId: field.id,
                              startMouseX: e.clientX,
                              startMouseY: e.clientY,
                              startWidth: rawWidth,
                              startHeight: rawHeight,
                            };
                          }}
                        />
                      </div>
                    );
                  })}

                  {editorMode === 'fields' && selectedFieldId && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-blue-700/90 text-white text-[11px] px-3 py-1 rounded-full pointer-events-none whitespace-nowrap">
                      Drag box to move · Drag right edge to resize width · Drag bottom edge to resize height
                    </div>
                  )}
                  {editorMode === 'whiteout' && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-red-700/90 text-white text-[11px] px-3 py-1 rounded-full pointer-events-none whitespace-nowrap">
                      Drag to draw a white-out box over junk · × removes a box · Save White-Out when done
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel — Properties */}
          <div className="w-60 border-l border-neutral-200 bg-white overflow-y-auto flex-shrink-0">
            {selectedField ? (
              <div className="p-3 space-y-3">
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium text-sm">Properties</span>
                  <button onClick={() => deleteField(selectedField.id)} className="text-red-500 hover:text-red-700 text-xs">Delete</button>
                </div>

                {/* Field Source */}
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Field Type</label>
                  <select
                    value={selectedField.fieldSource}
                    onChange={e => updateField(selectedField.id, { fieldSource: e.target.value })}
                    className="w-full px-2 py-1.5 border border-neutral-200 rounded text-xs"
                  >
                    {FIELD_SOURCES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>

                {/* Field Label / Static Value / Display Name */}
                {selectedField.fieldSource === 'static_text' ? (
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">
                      Static Value <span className="text-amber-600 font-semibold">*</span>
                    </label>
                    <input
                      type="text"
                      value={selectedField.fieldName || ''}
                      onChange={e => updateField(selectedField.id, { fieldName: e.target.value })}
                      placeholder='e.g., AGENT or Christopher Vineyard'
                      className="w-full px-2 py-1.5 border border-amber-400 bg-amber-50 rounded text-xs font-medium"
                    />
                    <p className="text-[10px] text-amber-700 mt-0.5">This exact text prints on every form — locked, never blank.</p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">
                      {selectedField.fieldSource === 'manual_entry' ? 'Field Label (shown when printing) *' : 'Display Name'}
                    </label>
                    <input
                      type="text"
                      value={selectedField.fieldName || ''}
                      onChange={e => updateField(selectedField.id, { fieldName: e.target.value })}
                      placeholder={selectedField.fieldSource === 'manual_entry' ? 'e.g., Odometer Reading' : 'Optional'}
                      className={`w-full px-2 py-1.5 border rounded text-xs ${selectedField.fieldSource === 'manual_entry' ? 'border-blue-300 bg-blue-50' : 'border-neutral-200'}`}
                    />
                  </div>
                )}

                {/* Page + Font Size */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Page</label>
                    <input type="number" min="1" value={selectedField.pageNumber}
                      onChange={e => updateField(selectedField.id, { pageNumber: parseInt(e.target.value) || 1 })}
                      className="w-full px-2 py-1.5 border border-neutral-200 rounded text-xs" />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Font Size</label>
                    <input type="number" min="6" max="24" value={selectedField.fontSize}
                      onChange={e => updateField(selectedField.id, { fontSize: parseInt(e.target.value) || 9 })}
                      className="w-full px-2 py-1.5 border border-neutral-200 rounded text-xs" />
                  </div>
                </div>

                {/* X / Y display (read-only hint) */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">X</label>
                    <input type="number" value={selectedField.xPosition}
                      onChange={e => updateField(selectedField.id, { xPosition: parseFloat(e.target.value) || 0 })}
                      className="w-full px-2 py-1.5 border border-neutral-200 rounded text-xs" />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Y</label>
                    <input type="number" value={selectedField.yPosition}
                      onChange={e => updateField(selectedField.id, { yPosition: parseFloat(e.target.value) || 0 })}
                      className="w-full px-2 py-1.5 border border-neutral-200 rounded text-xs" />
                  </div>
                </div>

                {/* Field Width / Height */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Width <span className="text-neutral-400">(pts)</span></label>
                    <input type="number" value={selectedField.maxWidth || 120}
                      onChange={e => updateField(selectedField.id, { maxWidth: e.target.value ? parseFloat(e.target.value) : 120 })}
                      min="20" max="600"
                      placeholder="120" className="w-full px-2 py-1.5 border border-neutral-200 rounded text-xs" />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Height <span className="text-neutral-400">(pts)</span></label>
                    <input type="number"
                      value={selectedField.fieldHeight || Math.max((selectedField.fontSize || 9) * 2, 14)}
                      onChange={e => updateField(selectedField.id, { fieldHeight: e.target.value ? parseFloat(e.target.value) : null })}
                      min="8" max="200"
                      placeholder="auto" className="w-full px-2 py-1.5 border border-neutral-200 rounded text-xs" />
                  </div>
                </div>
                <p className="text-[10px] text-neutral-400 -mt-1">Drag right/bottom edges on the PDF to resize visually</p>

                {/* Transform */}
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Transform</label>
                  <select value={selectedField.transform || ''} onChange={e => updateField(selectedField.id, { transform: e.target.value || null })}
                    className="w-full px-2 py-1.5 border border-neutral-200 rounded text-xs">
                    <option value="">None</option>
                    <option value="uppercase">UPPERCASE</option>
                    <option value="currency">Currency ($X,XXX.XX)</option>
                    <option value="date">Date Format</option>
                    <option value="number">Number Format</option>
                  </select>
                </div>

                {/* Bold */}
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={selectedField.isBold}
                    onChange={e => updateField(selectedField.id, { isBold: e.target.checked })} />
                  Bold
                </label>
              </div>
            ) : (
              <div className="p-4 text-center text-neutral-400 text-xs mt-8">
                <div className="mb-2 text-2xl">👆</div>
                Click a field in the list<br/>or click "+ Add" to start
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormFieldEditor;
